import React from 'react';
import { Box, Badge, SpaceBetween } from '@cloudscape-design/components';

/**
 * Banner shown INSIDE a storefront summarizing the agent's interactions with THIS
 * seller. Two cases:
 *   - served/paid: the agent fetched (and maybe paid for) content → list it.
 *   - lost: the seller BLOCKED the agent, so the sale went to a competitor → show
 *     that the seller earned nothing (do NOT pretend it served content).
 */
function summarize(r) {
  const c = r.content || {};
  if (Array.isArray(c.results) && c.results.length) {
    const top = c.results[0];
    return `${c.topic ? `“${c.topic}” — ` : ''}${top.title || top.name || `${c.results.length} results`}`;
  }
  if (Array.isArray(c.picks)) return `shopping list · ${c.picks.length} items${c.total ? ` · $${c.total}` : ''}`;
  if (Array.isArray(c.articles)) return `${c.briefing ? 'briefing' : 'coverage'}${c.topic ? ` on ${c.topic}` : ''} · ${c.articles.length} stories`;
  if (Array.isArray(c.games)) return `scores · ${c.games.length} games`;
  if (c.title || c.brand) return `${c.brand || ''} ${c.title || ''}`.trim();
  if (c.topic) return c.topic;
  return r.skillId || r.action || 'request';
}

function isLost(r) {
  return String(r.outcome || '').startsWith('lost') || String(r.outcome || '').startsWith('blocked');
}

export default function AgentRevealBanner({ reveals, accent = '#0972d3' }) {
  if (!reveals || reveals.length === 0) return null;
  const served = reveals.filter((r) => !isLost(r));
  const lost = reveals.filter(isLost);

  return (
    <SpaceBetween size="xs">
      {served.length > 0 && (
        <div style={{ border: `1px solid ${accent}55`, background: `${accent}11`, borderRadius: 8, padding: '8px 12px' }}>
          <SpaceBetween size="xxs">
            <Box fontSize="body-s" fontWeight="bold">
              🤖 Your agent fetched {served.length} item{served.length > 1 ? 's' : ''} from this seller
            </Box>
            {served.slice(-3).map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
                <span>{summarize(r)}</span>
                <Badge color={String(r.outcome || '').startsWith('paid') ? 'green' : 'grey'}>
                  {String(r.outcome || '').startsWith('paid') ? 'paid · served' : 'served'}
                </Badge>
              </div>
            ))}
          </SpaceBetween>
        </div>
      )}

      {lost.length > 0 && (
        <div style={{ border: '1px solid #d9151555', background: '#fff0f0', borderRadius: 8, padding: '8px 12px' }}>
          <Box fontSize="body-s" fontWeight="bold">
            🚫 WAF blocked the agent {lost.length} time{lost.length > 1 ? 's' : ''} — {lost.length} sale{lost.length > 1 ? 's' : ''} lost to a competitor
          </Box>
          <Box fontSize="body-s" color="text-body-secondary">
            This seller served no content and earned no revenue. Switch the edge to <b>Monetized</b> to capture it.
          </Box>
        </div>
      )}
    </SpaceBetween>
  );
}
